import React, { useCallback, useState } from "react";
import { Table, Button } from "react-bootstrap";
import useSWR from "swr";
import { Link, useNavigate } from "react-router-dom";
import { serverUrl, fetcher, swrConfig } from "../helpers/utility";
import variables from "../styles/utils.module.scss";
import Loading from "./loading";
import TabWithFilter from "./TabWithFilter";
import { useForm } from "react-hook-form";
import { ArrowRightIcon } from 'lucide-react';

export default function PlacementsContent({ summaryOnly = false }) {
  const navigate = useNavigate();
  const steps = { ACTIVE: "ACTIVE", COMPLETED: "COMPLETED", CANCELLED: "CANCELLED" };
  const [step, setStep] = useState(steps.ACTIVE);
  const { register } = useForm();

  const getPlacementUrl = () => {
    const baseUrl = `${serverUrl}/placement`;
    if (step === steps.COMPLETED) {
      return `${baseUrl}?status=completed`;
    } else if (step === steps.CANCELLED) {
      return `${baseUrl}?status=cancelled`;
    }
    return `${baseUrl}?status=active`;
  };

  const getCostFrequency = (frequency) => {
    return frequency === 'per_week' ? 'per week' : 'per month';
  };

  const {
    data: placementData,
    isLoading: placementIsLoading,
  } = useSWR(getPlacementUrl(), fetcher, swrConfig);

  function hasPlacements() {
    return placementData && placementData?.placements?.length > 0;
  }
 
  function openPlacementCount() {
    return placementData?.placements?.filter((placement) => placement.status === steps.ACTIVE).length;
  }

  const newPlacement = useCallback(() => {
    navigate(`/add-placement`);
  }, [navigate]);

  const goToPlacement = useCallback(
    ({ placementId }) => {
      navigate(`/add-placement?placementId=${placementId}`);
    },
    [navigate]
  );

  const goToPlacementSearch = useCallback(
    ({ placementId }) => {
      navigate(`/search?placementId=${placementId}`);
    },
    [navigate]
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="d-flex flex-row mb-2">
        <h2 className="h3">Placements</h2>
        {summaryOnly ? (
          <p className="text-muted medium ms-auto">
            {placementData?.placements?.length} of {openPlacementCount()} active placements
          </p>
        ) : null}
      </div>
      <div className="space-y-4">
        <TabWithFilter
          tabs={[
            {
              label: "Active",
              onClick: () => {
                console.log(`Active`);
                setStep(steps.ACTIVE);
              },
              active: step === steps.ACTIVE,
            },
            ...(!summaryOnly
              ? [
                  {
                    label: "Placed",
                    onClick: () => {
                      console.log(`Completed`);
                      setStep(steps.COMPLETED);
                    },
                    active: step === steps.COMPLETED,
                  },
                  {
                    label: "Cancelled",
                    onClick: () => {
                      console.log(`Cancelled`);
                      setStep(steps.CANCELLED);
                    },
                    active: step === steps.CANCELLED,
                  },
                ]
              : []),
          ]}
          filterInputId={summaryOnly ? null : "reference"}
          filterRegister={register}
          filterPlaceholder="Reference"
        />

        {placementIsLoading ? <Loading inline={true} inlineHeight={300} /> : null}

        {step === steps.ACTIVE ? (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Known As</th>
                  <th className="d-none d-md-table-cell">Location</th>
                  <th style={{ width: "66px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {placementData?.placements?.length > 0 &&
                  placementData?.placements.map((placement) => {
                    return (
                      <tr
                        key={placement._id}
                        onClick={() => {
                          goToPlacementSearch({ placementId: placement._id });
                        }}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <td>{placement.reference}</td>
                        <td>{placement.firstname}</td>
                        <td className="d-none d-md-table-cell">{placement.location?.label}</td>
                        <td style={{ textAlign: "left" }}>
                          <i
                            className={`bi bi-pencil`}
                            style={{
                              fontSize: "14px",
                              color: variables.green,
                              padding: "3px",
                              marginRight: "6px",
                              cursor: "pointer",
                            }}
                            onClick={(event) => {
                              goToPlacement({ placementId: placement._id });
                              event.stopPropagation();
                            }}
                          ></i>

                          <i
                            className={`bi bi-search`}
                            style={{
                              fontSize: "14px",
                              color: variables.green,
                              padding: "3px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              goToPlacementSearch({ placementId: placement._id });
                            }}
                          ></i>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>

            <div className="d-flex flex-row justify-content-between align-items-center">
              <Button size="sm" variant="primary" onClick={newPlacement}>
                ADD PLACEMENT
              </Button>

              {summaryOnly ? (
                <Link className="ms-auto text-primary" to="/local-authority/dashboard/placements">
                  See all Placements <ArrowRightIcon />
                </Link>
              ) : null}
            </div>
          </>
        ) : null}

        {step === steps.COMPLETED ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Known As</th>
                <th className="d-none d-md-table-cell">Placed with</th>
                <th className="d-none d-md-table-cell">Placed on</th>
                <th className="d-none d-md-table-cell">
                  Cost
                </th>
                {/* <th style={{ width: "16px" }}>Actions</th> */}
              </tr>
            </thead>
            <tbody>
              {placementData?.placements?.length > 0 &&
                placementData?.placements.map((placement) => {
                  return (
                    <tr
                      key={placement._id}
                      onClick={() => {
                        // goToPlacementSearch({ placementId: placement._id });
                        console.log("PLACEMENT:", JSON.stringify(placement, null, 2));
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <td>{placement.reference}</td>
                      <td>{placement.firstname}</td>
                      <td className="d-none d-md-table-cell">
                        {placement.placementLocation?.name}
                      </td>
                      <td className="d-none d-md-table-cell">
                        {placement.placementDate
                          ? new Date(placement.placementDate).toLocaleDateString()
                          : ""}
                      </td>
                      <td className="d-none d-md-table-cell">
                        {placement.cost?.amount ? `£${placement.cost.amount} ${getCostFrequency(placement.cost?.frequency)}` : "N/A"}
                      </td>
                      {/* <td style={{ textAlign: "center" }}>
                        <i
                          className={`bi bi-eye`}
                          onClick={() => {
                            console.log("TODO: View placement");
                          }}
                          style={{
                            fontSize: "16px",
                            color: "white",
                            backgroundColor: variables.green,
                            borderRadius: "10%",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        ></i>
                      </td> */}
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        ) : null}

        {step === steps.CANCELLED ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ marginTop: 20, marginBottom: 10 }}
          >
            No results found.
            <div style={{ fontSize: 10, color: variables.grey }}>NOT IMPLEMENTED</div>
          </div>
        ) : null}

        {!placementIsLoading && hasPlacements() === false && step === steps.ACTIVE ? (
          <div className="d-flex flex-column justify-content-center" style={{ marginBottom: 10 }}>
            Welcome! Get started by adding your first placement.
          </div>
        ) : null}
      </div>
    </div>
  );
}

